import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { userService, getErrorMessage } from '../../services/apiService';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import Icon from '../../components/AppIcon';
import UserTable from './components/UserTable';
import UserFormModal from './components/UserFormModal';
import UserFilters from './components/UserFilters';
import ResetPasswordModal from './components/ResetPasswordModal';
import { useToast } from '../../components/ui/Toast';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const toast = useToast();
  const { confirmState, confirm, setConfirmState } = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        role: filters.role,
      };

      // Transform status filter to isActive boolean
      if (filters.status === 'active') {
        params.isActive = true;
      } else if (filters.status === 'inactive') {
        params.isActive = false;
      }

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await userService.getUsers(params);
      
      if (response.success) {
        setUsers(response.data);
        if (response.meta) {
          setPagination(prev => ({
            ...prev,
            total: response.meta.total,
            totalPages: response.meta.totalPages,
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.limit, filters]);

  // Handle create user
  const handleCreate = () => {
    setSelectedUser(null);
    setModalMode('create');
    setShowModal(true);
  };

  // Handle edit user
  const handleEdit = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    setShowModal(true);
  };

  // Handle delete user
  const handleDelete = async (userId) => {
    const confirmed = await confirm({
      title: 'Deactivate User',
      message: 'Are you sure you want to deactivate this user?',
      variant: 'warning',
      confirmText: 'Deactivate',
      cancelText: 'Cancel'
    });

    if (!confirmed) return;

    try {
      await userService.deleteUser(userId);
      fetchUsers();
      toast.success('User deactivated successfully');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Handle reset password
  const handleResetPassword = (user) => {
    setResetPasswordUser(user);
    setShowResetPasswordModal(true);
  };

  // Handle reset password submit
  const handleResetPasswordSubmit = async (newPassword) => {
    try {
      await userService.resetPassword(resetPasswordUser.id, newPassword);
      toast.success(`Password reset successfully for ${resetPasswordUser.full_name || resetPasswordUser.email}`);
      setShowResetPasswordModal(false);
    } catch (err) {
      throw err; // Let the modal handle the error
    }
  };

  // Handle form submit (create/edit)
  const handleFormSubmit = async (formData) => {
    try {
      if (modalMode === 'create') {
        await userService.createUser(formData);
      } else {
        await userService.updateUser(selectedUser.id, formData);
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      throw err; // Let the modal handle the error
    }
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  if (loading && users.length === 0) {
    return (
      <div className="space-y-5">
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div>
                <p className="mt-4 text-slate-600">Loading users...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <Breadcrumb 
        items={[
          { label: 'Dashboard', path: '/dashboard-overview' },
          { label: 'User Management', path: '/user-management' }
        ]} 
      />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            User Management
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Manage system users and their roles ({pagination.total} users)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="default"
            onClick={handleCreate}
            iconName="UserPlus"
            iconPosition="left"
          >
            Add User
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600" />
              <p className="text-red-800">{error}</p>
              <Button variant="outline" size="sm" onClick={fetchUsers}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <UserFilters
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {/* Users Table */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-0">
          <UserTable
            users={users}
            currentUserId={currentUser?.id}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onResetPassword={handleResetPassword}
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <UserFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={handleFormSubmit}
          user={selectedUser}
          mode={modalMode}
        />
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <ResetPasswordModal
          isOpen={showResetPasswordModal}
          onClose={() => setShowResetPasswordModal(false)}
          onSubmit={handleResetPasswordSubmit}
          user={resetPasswordUser}
        />
      )}
      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />
    </div>
  );
};

export default UserManagement;







